import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Alert, Platform, Linking } from 'react-native';
import SplashScreen from './src/components/SplashScreen';
import LoginScreen from './src/screens/LoginScreen';
import DashboardScreen from './src/screens/DashboardScreen';
import CompanySetupScreen from './src/screens/CompanySetupScreen';
import PackageSelectionScreen from './src/screens/PackageSelectionScreen';
import SuperAdminScreen from './src/screens/SuperAdminScreen';
import ScanBusinessCardScreen from './src/screens/ScanBusinessCardScreen';
import SettingsScreen from './src/screens/SettingsScreen';
import ServiceManagementScreen from './src/screens/ServiceManagementScreen';
import ProductManagementScreen from './src/screens/ProductManagementScreen';
import QRCodePaymentScreen from './src/screens/QRCodePaymentScreen';
import BankAccountSetupScreen from './src/screens/BankAccountSetupScreen';
import PayoutDashboardScreen from './src/screens/PayoutDashboardScreen';
import BusinessSetupPaymentScreen from './src/screens/BusinessSetupPaymentScreen';
import BookAPTChatScreen from './src/screens/BookAPTChatScreen';
import BusinessChatScreen from './src/screens/BusinessChatScreen';
import SendInvoiceScreen from './src/screens/SendInvoiceScreen';
import UserDashboardScreen from './src/screens/UserDashboardScreen';
import UserFavoritesScreen from './src/screens/UserFavoritesScreen';
import BusinessExplorationScreen from './src/screens/BusinessExplorationScreen';
import BusinessDetailScreen from './src/screens/BusinessDetailScreen';
import { Business } from './src/types/business';
import UserTypeSelectionScreen from './src/screens/UserTypeSelectionScreen';
import CareRoleSelectionScreen from './src/screens/CareRoleSelectionScreen';
import CareDashboardScreen from './src/screens/CareDashboardScreen';
import CareNotesScreen from './src/screens/CareNotesScreen';
import CareInviteAcceptScreen from './src/screens/CareInviteAcceptScreen';
import StaffInviteAcceptScreen from './src/screens/StaffInviteAcceptScreen.tsx';
import UserProfileScreen from './src/screens/UserProfileScreen';
import CustomerRegistrationScreen from './src/screens/CustomerRegistrationScreen';
import type { CareRole } from './src/services/unifiedAuthService';
import CustomerManagementScreen from './src/screens/CustomerManagementScreen';
import WidgetDownloadScreen from './src/screens/WidgetDownloadScreen';
import WidgetEmbedBookingScreen from './src/screens/WidgetEmbedBookingScreen';
import QuoteRequestsScreen from './src/screens/QuoteRequestsScreen';
import GuestWelcomeScreen from './src/screens/GuestWelcomeScreen';
import GuestDirectBookingScreen from './src/screens/GuestDirectBookingScreen';
import GuestBusinessBookingScreen from './src/screens/GuestBusinessBookingScreen';
import { isWidgetEmbedBookingEntry, isPayLinkEntry, isJoinBusinessEntry, consumeBookingsHandoffToken } from './src/utils/widgetEmbed';
import PayLinkScreen from './src/screens/PayLinkScreen';
import type { InstantBooking } from './src/services/instantBookingService';
import { AuthManager } from './src/utils/authManager';
import { UnifiedAuthService } from './src/services/unifiedAuthService';
import SimpleStorage from './src/utils/simpleStorage';
import welcomeEmailService from './src/services/welcomeEmailService';
import PrepayBookingE2EHarness from './src/e2e/PrepayBookingE2EHarness';
import {
  refreshBusinessCalendarAutoSync,
  stopBusinessCalendarAutoSync,
} from './src/services/businessCalendarAutoSyncService';
import customerWelcomeEmailService from './src/services/customerWelcomeEmailService';
import { STRIPE_CONFIG as APP_STRIPE_CONFIG } from './src/config/stripeConfig';
import MobileAppBanner from './src/components/MobileAppBanner';
// import { firebaseService } from './src/services/firebaseService';

let StripeProvider: any = null;
if (Platform.OS !== 'web') {
  try {
    const stripeNative = require('@stripe/stripe-react-native');
    StripeProvider = stripeNative?.StripeProvider || null;
  } catch (error) {
    console.warn('StripeProvider not available:', error);
  }
}

// update here: add/remove screen names when you add or drop flows
type Screen = 'guest-welcome' | 'guest-explore' | 'guest-booking' | 'guest-book-business' | 'login' | 'user-type-selection' | 'care-role-selection' | 'customer-registration' | 'company-setup' | 'package-selection' | 'dashboard' | 'care-dashboard' | 'care-notes' | 'care-invite-accept' | 'staff-invite-accept' | 'user-profile' | 'super-admin' | 'settings' | 'service-management' | 'product-management' | 'qr-code' | 'customer-management' | 'payment-providers' | 'bank-setup' | 'payout-dashboard' | 'chat' | 'customer-chat' | 'business-chat' | 'send-invoice' | 'user-dashboard' | 'user-favorites' | 'business-exploration' | 'business-detail' | 'widget-download' | 'quote-requests' | 'scan-business-card';

// Wrapper component to load business owner data for payment setup
const PaymentProvidersWrapper: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [businessOwner, setBusinessOwner] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const loadBusinessOwner = async () => {
      try {
        const owner = await AuthManager.getSavedBusinessOwner();
        if (owner) {
          setBusinessOwner(owner);
        }
      } catch (error) {
        console.error('Error loading business owner:', error);
      } finally {
        setLoading(false);
      }
    };
    loadBusinessOwner();
  }, []);

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <Text>Loading...</Text>
      </View>
    );
  }

  return (
    <BusinessSetupPaymentScreen
      businessId={businessOwner?.id || ''}
      businessName={businessOwner?.name || businessOwner?.companyName || 'Your Business'}
      onComplete={() => {
        Alert.alert('Success', 'Payment provider connected successfully! You can now accept payments from customers.');
        onBack();
      }}
      onSkip={onBack}
    />
  );
};

const App: React.FC = () => {
  const isBusinessOwnerUserType = (value: string | null | undefined): boolean => {
    return value === 'business_owner' || value === 'business';
  };

  const [webE2ePrepay] = useState(() => {
    if (Platform.OS === 'web' && typeof globalThis !== 'undefined') {
      try {
        const loc = (globalThis as { location?: { search?: string } }).location;
        const search = loc?.search;
        if (search) {
          const match = search.match(/[?&]e2e=([^&]*)/);
          const value = match ? decodeURIComponent(match[1]) : null;
          return value === 'prepay-buttons';
        }
      } catch {
        return false;
      }
    }
    return false;
  });

  const [showSplash, setShowSplash] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState<string>('');
  const [currentScreen, setCurrentScreen] = useState<Screen>('guest-welcome');
  const [guestBookBusiness, setGuestBookBusiness] = useState<Business | null>(null);
  // update here: add new user types (e.g. 'staff', 'admin') and handle in handleLoginSuccess + renderScreen
  const [userType, setUserType] = useState<'customer' | 'business-owner' | 'super-admin'>('customer');
  /** When userType === 'customer', optional care role (Family / Senior / Care professional). */
  const [careRole, setCareRole] = useState<CareRole | null>(null);
  /** Set when user chooses Care then a role, before completing registration; passed to CustomerRegistrationScreen. */
  const [pendingCareRole, setPendingCareRole] = useState<CareRole | null>(null);
  const [careNotesRecipientId, setCareNotesRecipientId] = useState<string>('');
  const [careNotesRecipientName, setCareNotesRecipientName] = useState<string>('');
  const [careNotesCanEdit, setCareNotesCanEdit] = useState(false);
  const [careInviteParams, setCareInviteParams] = useState<{
    recipientId: string;
    memberId: string;
    token: string;
  } | null>(null);
  const [staffInviteParams, setStaffInviteParams] = useState<{
    inviteId: string;
    token: string;
  } | null>(null);
  const [triggerOneClickBooking, setTriggerOneClickBooking] = useState(false);
  /** When user taps Book on Favorites from App-routed `user-favorites` (e.g. from Chat nav), open booking on dashboard. */
  const [pendingBookFromFavorites, setPendingBookFromFavorites] = useState<Business | null>(null);
  /** Widget / front-desk: ?widgetBook=1&businessId= or ?source=frontdesk_qr&businessId= — consumed on customer dashboard after login. */
  const [widgetBookingParams, setWidgetBookingParams] = useState<{
    businessId: string;
    serviceId?: string;
  } | null>(null);
  const [companyData, setCompanyData] = useState<any>(null); // Store company data for welcome email
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null); // Store selected business for BusinessDetailScreen
  /** After guest booking, open customer dashboard on Appointments tab. */
  const [dashboardInitialTab, setDashboardInitialTab] = useState<'dashboard' | 'appointments' | undefined>();

  /**
   * True when the page just loaded from a Stripe Checkout redirect
   * (?success=true&session_id=...) — e.g. after purchasing a product
   * subscription plan. currentScreen always defaults to 'guest-welcome' on
   * a fresh page load and nothing else reads the URL, so without this the
   * app boots straight to the dashboard and PackageSelectionScreen (the only
   * place that calls /checkout/verify to actually activate the plan) never
   * mounts — the purchase silently never takes effect.
   */
  const hasStripeCheckoutReturn = (): boolean => {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || !window.location) return false;
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('success') === 'true' && !!params.get('session_id');
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Initialize SimpleStorage FIRST before anything else
        console.log('🚀 App: Initializing SimpleStorage...');
        await SimpleStorage.forceInitialize();
        console.log('✅ App: SimpleStorage initialized');
        
        // Test data initialization disabled for production

        // A new tab opened via "see your bookings on BookAPT" (widget
        // booking confirmation) carries a one-time sign-in handoff token —
        // consume it before the normal auth check below, so this tab lands
        // signed in as the guest account that was just created, not at a
        // login wall.
        if (Platform.OS === 'web') {
          const handoffToken = consumeBookingsHandoffToken();
          if (handoffToken) {
            try {
              const { signInWithCustomToken } = require('firebase/auth');
              const { getWebAuth } = require('./src/services/unifiedAuthService');
              await signInWithCustomToken(getWebAuth(), handoffToken);
              console.log('✅ App: Signed in via bookings handoff token');
            } catch (handoffError) {
              console.warn('⚠️ App: Bookings handoff sign-in failed:', handoffError);
            }
          }
        }

        // Check Firebase authentication status first (with error handling)
        console.log('🔐 App: Checking Firebase auth status...');
        try {
          const firebaseUser = await UnifiedAuthService.getCurrentUser();
          
          if (firebaseUser) {
            console.log('✅ App: Firebase user found:', firebaseUser.email);
            setIsAuthenticated(true);
            setUsername(firebaseUser.email || firebaseUser.name || 'User');
            setUserType(isBusinessOwnerUserType(firebaseUser.userType) ? 'business-owner' : 'customer');
            const role = firebaseUser.careRole ?? null;
            setCareRole(role);
            if (isBusinessOwnerUserType(firebaseUser.userType)) {
              setCurrentScreen(hasStripeCheckoutReturn() ? 'package-selection' : 'dashboard');
            } else if (role) {
              setCurrentScreen('care-dashboard');
            } else if (!isWidgetEmbedBookingEntry()) {
              setCurrentScreen('user-dashboard');
            }
            return;
          }
        } catch (firebaseError) {
          console.warn('⚠️ Firebase auth check failed, falling back to local storage:', firebaseError);
          // Continue to local storage check below
        }
        
        // Fallback: Check local storage authentication (for backward compatibility)
        console.log('🔐 App: No Firebase user, checking local storage...');
        const storedUser = await SimpleStorage.getItem('currentUser');
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          setIsAuthenticated(true);
          setUsername(userData.username || '');
          setUserType(userData.userType || 'business-owner');
          setCurrentScreen(hasStripeCheckoutReturn() ? 'package-selection' : 'dashboard');
        } else {
          setIsAuthenticated(false);
          if (isJoinBusinessEntry()) {
            setCurrentScreen('user-type-selection');
          } else if (!isWidgetEmbedBookingEntry()) {
            setCurrentScreen('guest-welcome');
          }
        }
      } catch (error) {
        console.error('❌ Error checking auth:', error);
        setIsAuthenticated(false);
        if (isJoinBusinessEntry()) {
          setCurrentScreen('user-type-selection');
        } else if (!isWidgetEmbedBookingEntry()) {
          setCurrentScreen('guest-welcome');
        }
      }
    };

    checkAuth();
    
    // Listen to Firebase auth state changes
    const unsubscribe = UnifiedAuthService.onAuthStateChanged((user) => {
      if (user) {
        console.log('🔐 App: Firebase auth state changed - user logged in:', user.email);
        setIsAuthenticated(true);
        setUsername(user.email || user.name || 'User');
        setUserType(isBusinessOwnerUserType(user.userType) ? 'business-owner' : 'customer');
        const role = user.careRole ?? null;
        setCareRole(role);
        if (isBusinessOwnerUserType(user.userType)) {
          setCurrentScreen(hasStripeCheckoutReturn() ? 'package-selection' : 'dashboard');
        } else if (role) {
          setCurrentScreen('care-dashboard');
        } else if (!isWidgetEmbedBookingEntry()) {
          setCurrentScreen('user-dashboard');
        }
      } else {
        console.log('🔐 App: Firebase auth state changed - user logged out');
        setIsAuthenticated(false);
        setUsername('');
        if (isJoinBusinessEntry()) {
          setCurrentScreen('user-type-selection');
        } else if (!isWidgetEmbedBookingEntry()) {
          setCurrentScreen('guest-welcome');
        }
      }
    });
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Parse care-invite URL on load (deep link or web URL)
  useEffect(() => {
    const getParam = (search: string, key: string): string | null => {
      const match = new RegExp(`[?&]${key}=([^&]*)`).exec(search);
      return match ? decodeURIComponent(match[1]) : null;
    };
    const parseCareInviteUrl = (url: string | null) => {
      if (!url || !url.includes('care-invite')) return;
      try {
        const r = getParam(url, 'recipientId');
        const m = getParam(url, 'memberId');
        const t = getParam(url, 'token');
        if (r && m && t) {
          setCareInviteParams({ recipientId: r, memberId: m, token: t });
          setCurrentScreen('care-invite-accept');
        }
      } catch {
        // ignore
      }
    };
    const parseStaffInviteUrl = (url: string | null) => {
      if (!url || !url.includes('staff-invite')) return;
      try {
        const inviteId = getParam(url, 'inviteId');
        const token = getParam(url, 'token');
        if (inviteId && token) {
          setStaffInviteParams({ inviteId, token });
          setCurrentScreen('staff-invite-accept');
        }
      } catch {
        // ignore
      }
    };
    const parseWidgetBookingUrl = (href: string | null) => {
      if (!href) return;
      try {
        const win = (typeof (globalThis as any).window !== 'undefined' && (globalThis as any).window) as
          | { location?: { pathname?: string } }
          | undefined;
        const path = win?.location?.pathname || '';
        const legacyBookPath = path === '/book' || path.endsWith('/book');
        const search = href.indexOf('?') >= 0 ? href.slice(href.indexOf('?')) : '';
        const widgetBook = search ? getParam(search, 'widgetBook') : null;
        const source = search ? getParam(search, 'source') : null;
        // Accept camelCase and lowercase params from older widget links.
        const businessId = search
          ? (getParam(search, 'businessId') || getParam(search, 'businessid'))
          : null;
        const serviceId = search
          ? (getParam(search, 'serviceId') || getParam(search, 'serviceid'))
          : null;
        const widgetOk = widgetBook === '1' || widgetBook === 'true';
        const fromFlag = widgetOk && !!businessId;
        const fromLegacyPath = legacyBookPath && !!businessId;
        const fromFrontDeskWeb = source === 'frontdesk_qr' && !!businessId;
        if (businessId && fromFlag) {
          setWidgetBookingParams(serviceId ? { businessId, serviceId } : { businessId });
        } else if (businessId && fromLegacyPath) {
          setWidgetBookingParams(serviceId ? { businessId, serviceId } : { businessId });
        } else if (businessId && fromFrontDeskWeb) {
          setWidgetBookingParams(serviceId ? { businessId, serviceId } : { businessId });
        }
      } catch {
        // ignore
      }
    };
    if (Platform.OS === 'web') {
      const w = (typeof (globalThis as any).window !== 'undefined' && (globalThis as any).window) as { location?: { href?: string } } | undefined;
      if (w?.location?.href) {
        parseCareInviteUrl(w.location.href);
        parseStaffInviteUrl(w.location.href);
        parseWidgetBookingUrl(w.location.href);
      }
    } else {
      Linking.getInitialURL().then((u) => {
        parseCareInviteUrl(u);
        parseStaffInviteUrl(u);
        parseWidgetBookingUrl(u);
      });
      const sub = Linking.addEventListener('url', (e) => {
        parseCareInviteUrl(e.url);
        parseStaffInviteUrl(e.url);
        parseWidgetBookingUrl(e.url);
      });
      return () => sub.remove();
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (!isAuthenticated || userType !== 'business-owner') {
        stopBusinessCalendarAutoSync();
        return;
      }
      await refreshBusinessCalendarAutoSync();
    })();
  }, [isAuthenticated, userType]);

  const handleLoginSuccess = async (
    loginType: 'customer' | 'business-owner' | 'super-admin' = 'customer',
    careRoleFromRegistration?: CareRole
  ) => {
    console.log('🔍 App.handleLoginSuccess called with loginType:', loginType, 'careRole:', careRoleFromRegistration);
    setIsAuthenticated(true);
    setUserType(loginType);
    if (careRoleFromRegistration != null) setCareRole(careRoleFromRegistration);

    // Get username after login - try Firebase first, then fallback to local storage
    try {
      // Try Firebase Auth first
      const firebaseUser = await UnifiedAuthService.getCurrentUser();

      if (firebaseUser) {
        console.log('✅ App: Got user from Firebase:', firebaseUser.email);
            setUsername(firebaseUser.email || firebaseUser.name || 'User');
        setUserType(isBusinessOwnerUserType(firebaseUser.userType) ? 'business-owner' : 'customer');
        const role = firebaseUser.careRole ?? careRoleFromRegistration ?? null;
        setCareRole(role);
        if (isBusinessOwnerUserType(firebaseUser.userType)) {
          setCurrentScreen('dashboard');
        } else if (careInviteParams) {
          setCurrentScreen('care-invite-accept');
        } else if (staffInviteParams) {
          setCurrentScreen('staff-invite-accept');
        } else if (role) {
          setCurrentScreen('care-dashboard');
        } else {
          setCurrentScreen('user-dashboard');
        }
        return;
      }
      
      // Fallback to local storage (for test users and business owners)
      if (loginType === 'customer') {
        // Use UnifiedAuthService for web, AuthManager for mobile
        let savedUser: any = null;
        if (Platform.OS === 'web') {
          savedUser = await UnifiedAuthService.getCurrentUser();
          console.log('🔍 App: Loaded user from UnifiedAuthService (web):', savedUser?.email);
        } else {
          savedUser = await AuthManager.getSavedUser();
          console.log('🔍 App: Loaded user from AuthManager (mobile):', savedUser?.email);
        }
        
        if (savedUser) {
          setUsername(savedUser.email || savedUser.name || 'User');
          console.log('✅ App: Set username from saved user:', savedUser.name || savedUser.email);
        } else {
          setUsername('User');
          console.warn('⚠️ App: No user found, using default username');
        }
        setUserType('customer');
        setCurrentScreen(careInviteParams ? 'care-invite-accept' : careRoleFromRegistration ? 'care-dashboard' : 'user-dashboard');
      } else if (loginType === 'business-owner') {
        const savedOwner = await AuthManager.getSavedBusinessOwner();
        if (savedOwner) {
          setUsername(savedOwner.email || savedOwner.companyName || savedOwner.name || 'Business Owner');
          console.log('✅ App: Business session loaded:', {
            email: savedOwner.email,
            companyName: savedOwner.companyName,
            businessId: savedOwner.id,
          });
        } else {
          setUsername('Business Owner');
          console.warn('⚠️ App: No business profile for this Firebase session (check Firestore or sign in again)');
        }
        setUserType('business-owner');
        setCurrentScreen('dashboard'); // Business Dashboard
      } else if (loginType === 'super-admin') {
        setUsername('Admin');
        setUserType('super-admin');
        setCurrentScreen('super-admin');
      }
    } catch (error) {
      console.error('Error getting username:', error);
      // Set default username on error
      setUsername(loginType === 'customer' ? 'User' : 'Business Owner');
    }
  };

  const handleLogout = async () => {
    stopBusinessCalendarAutoSync();
    try {
      // Logout from Firebase Auth
      await UnifiedAuthService.logoutUser();
      
      // Also clear local storage (for backward compatibility)
      await SimpleStorage.removeItem('currentUser');
      
      setIsAuthenticated(false);
      setUsername('');
      setCurrentScreen('guest-welcome');
      setUserType('customer');
    } catch (error) {
      console.error('Error signing out:', error);
      // Still logout locally even if Firebase signout fails
      setIsAuthenticated(false);
      setUsername('');
      setCurrentScreen('guest-welcome');
      setUserType('customer');
      setCareRole(null);
      setPendingCareRole(null);
    }
  };

  const handleSplashFinish = () => {
    setShowSplash(false);
  };

  const navigateToScreen = (screen: Screen) => {
    setCurrentScreen(screen);
  };

  const handleNavigateToSettings = () => {
    setCurrentScreen('settings');
  };

  const handleNavigateToServiceManagement = () => {
    setCurrentScreen('service-management');
  };

  const handleNavigateToProductManagement = () => {
    setCurrentScreen('product-management');
  };

  const handleNavigateToQRCode = () => {
    setCurrentScreen('qr-code');
  };

  const handleNavigateToBusinessChat = () => {
    setCurrentScreen('business-chat');
  };

  const handleNavigateToSendInvoice = () => {
    setCurrentScreen('send-invoice');
  };

  const handleNavigateToCustomerManagement = () => {
    setCurrentScreen('customer-management');
  };

  const handleNavigateToLinkedDevices = () => {
    // For now, just show an alert. You can implement linked devices screen later
    console.log('Navigate to linked devices');
  };

  const handleNavigateToPaymentProviders = () => {
    setCurrentScreen('payment-providers');
  };

  const handleNavigateToBankSetup = () => {
    setCurrentScreen('bank-setup');
  };

  const handleNavigateToPayoutDashboard = () => {
    setCurrentScreen('payout-dashboard');
  };

  const handleNavigateToWidgetDownload = () => {
    setCurrentScreen('widget-download');
  };

  const handleNavigateToQuoteRequests = () => {
    setCurrentScreen('quote-requests');
  };


  const handleNavigateToHelp = () => {
    // For now, just show an alert. You can implement help screen later
    Alert.alert('Help', 'Help and support functionality will be implemented here.');
  };

  const handleNavigateToUserTypeSelection = () => {
    setCurrentScreen('user-type-selection');
  };

  const handleUserTypeSelected = (userType: 'customer' | 'business-owner') => {
    if (userType === 'customer') {
      // Navigate to customer registration form
      setCurrentScreen('customer-registration');
    } else {
      // Navigate to business owner registration flow
      setCurrentScreen('company-setup');
    }
  };

  const handleNavigateToChat = () => {
    setCurrentScreen('chat');
  };

  const handleCompanySetupComplete = (companyData: any) => {
    setCompanyData(companyData); // Store company data for welcome email
    setCurrentScreen('package-selection');
  };

  const handlePackageSelectionComplete = async (packageData: any) => {
    // Send welcome email when business owner completes onboarding
    if (companyData && userType === 'business-owner') {
      try {
        // Get business owner info from AuthManager
        const businessOwnerData = await AuthManager.getSavedBusinessOwner();
        if (businessOwnerData || companyData.email) {
          const businessOwner = {
            id: businessOwnerData?.id || username || `business_${Date.now()}`,
            email: companyData.email || businessOwnerData?.email || username,
            businessName: companyData.companyName || businessOwnerData?.companyName || 'Your Business',
            name: businessOwnerData?.name || companyData.companyName || 'Business Owner',
          };

          // Send welcome email (async, don't block UI)
          welcomeEmailService.sendWelcomeEmail(businessOwner).catch(error => {
            console.error('Failed to send welcome email:', error);
            // Don't show error to user - email sending failure shouldn't block onboarding
          });
        }
      } catch (error) {
        console.error('Error preparing welcome email:', error);
        // Continue with onboarding even if email fails
      }
    }
    
    setCurrentScreen('dashboard');
  };

  const handleGuestBookingComplete = async (_booking: InstantBooking) => {
    setDashboardInitialTab('appointments');
    await handleLoginSuccess('customer');
  };

  const renderScreen = () => {
    if (Platform.OS === 'web' && isPayLinkEntry()) {
      return <PayLinkScreen />;
    }

    if (Platform.OS === 'web' && isWidgetEmbedBookingEntry()) {
      return <WidgetEmbedBookingScreen />;
    }

    if (!isAuthenticated) {
      switch (currentScreen) {
        case 'guest-explore':
          return (
            <BusinessExplorationScreen
              guestMode
              onBack={() => setCurrentScreen('guest-welcome')}
              onBookBusiness={(business) => {
                setGuestBookBusiness(business);
                setCurrentScreen('guest-book-business');
              }}
              onSignIn={() => setCurrentScreen('login')}
            />
          );
        case 'guest-booking':
          return (
            <GuestDirectBookingScreen
              onBack={() => setCurrentScreen('guest-welcome')}
              onBookingComplete={handleGuestBookingComplete}
            />
          );
        case 'guest-book-business':
          return guestBookBusiness ? (
            <GuestBusinessBookingScreen
              business={guestBookBusiness}
              onBack={() => setCurrentScreen('guest-explore')}
              onBookingComplete={handleGuestBookingComplete}
            />
          ) : (
            <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
              <Text>No business selected</Text>
            </View>
          );
        case 'guest-welcome':
        default:
          return (
            <GuestWelcomeScreen
              onExplore={() => setCurrentScreen('guest-explore')}
              onBook={() => setCurrentScreen('guest-booking')}
              onSignIn={() => setCurrentScreen('login')}
              onListBusiness={() => setCurrentScreen('user-type-selection')}
            />
          );
        case 'user-type-selection':
          return (
            <UserTypeSelectionScreen
              onBack={() => setCurrentScreen('guest-welcome')}
              onUserTypeSelected={handleUserTypeSelected}
              onCareSelected={() => setCurrentScreen('care-role-selection')}
            />
          );
        case 'care-role-selection':
          return (
            <CareRoleSelectionScreen
              onBack={() => setCurrentScreen('user-type-selection')}
              onCareRoleSelected={(role) => {
                setPendingCareRole(role);
                setCurrentScreen('customer-registration');
              }}
            />
          );
        case 'customer-registration':
          return (
            <CustomerRegistrationScreen
              onBack={() => {
                if (pendingCareRole) {
                  setPendingCareRole(null);
                  setCurrentScreen('care-role-selection');
                } else {
                  setCurrentScreen('user-type-selection');
                }
              }}
              onRegistrationComplete={() => {
                handleLoginSuccess('customer', pendingCareRole ?? undefined);
                setPendingCareRole(null);
              }}
              careRole={pendingCareRole ?? undefined}
            />
          );
        case 'company-setup':
          return (
            <CompanySetupScreen
              onBack={() => setCurrentScreen('user-type-selection')}
              onComplete={async (companyData) => {
                // Register business owner with Firebase
                try {
                  console.log('🔐 App: Registering business owner with Firebase...');
                  
                  // Register with Firebase Auth and Firestore using the password from form
                  const user = await UnifiedAuthService.registerUser(
                    companyData.email,
                    companyData.password,
                    {
                      name: companyData.companyName,
                      phone: companyData.phone,
                      userType: 'business_owner',
                      address: {
                        street: companyData.address,
                        city: companyData.city,
                        state: companyData.state,
                        zipcode: companyData.zipcode,
                        country: companyData.country,
                      },
                    }
                  );
                  
                  console.log('✅ App: Business owner registered in Firebase:', user.email);
                  
                  // Save business owner data locally with Firebase ID
                  const businessOwner = {
                    id: user.id,
                    email: user.email,
                    name: companyData.companyName,
                    username: user.email,
                    password: companyData.password, // Store the actual password for login
                    phone: companyData.phone,
                    address: {
                      street: companyData.address,
                      city: companyData.city,
                      state: companyData.state,
                      zipcode: companyData.zipcode,
                      country: companyData.country,
                    },
                    userType: 'business' as const,
                    companyName: companyData.companyName,
                    categoryId: companyData.categoryId,
                    industry: companyData.category || 'General',
                    companySize: 'Small (1-10 employees)', // Default, can be updated later
                    website: companyData.website,
                    description: companyData.description,
                    registrationDate: new Date().toISOString(),
                    businessHours: companyData.businessHours, // Save business hours
                    appointmentDuration: companyData.appointmentDuration, // Save appointment duration
                  };
                  
                  await AuthManager.saveBusinessOwner(businessOwner);
                  console.log('✅ App: Business owner saved locally');
                  
                  // Save business to Firestore businesses collection for searchability
                  try {
                    if (Platform.OS === 'web') {
                      const { doc, setDoc } = await import('firebase/firestore');
                      const { initializeFirebase, getWebDb } = await import('./src/services/unifiedAuthService');
                      
                      // Initialize Firebase if not already initialized
                      const initialized = initializeFirebase();
                      const db = getWebDb();
                      
                      if (initialized && db) {
                        const businessDoc = {
                          id: user.id,
                          ownerId: user.id,
                          name: companyData.companyName,
                          description: companyData.description || '',
                          categoryId: companyData.categoryId,
                          category: companyData.categoryId || companyData.category || 'General',
                          industry: companyData.category || 'General',
                          address: {
                            street: companyData.address,
                            city: companyData.city,
                            state: companyData.state,
                            zipcode: companyData.zipcode,
                            country: companyData.country,
                          },
                          phone: companyData.phone,
                          email: user.email,
                          website: companyData.website || '',
                          isActive: true,
                          isOnBookAPT: true, // Business registered on BookAPT platform
                          createdAt: new Date().toISOString(),
                          updatedAt: new Date().toISOString(),
                        };
                        
                        await setDoc(doc(db, 'businesses', user.id), businessDoc);
                        console.log('✅ App: Business saved to Firestore businesses collection');
                      } else {
                        console.warn('⚠️  Firebase not initialized - business not saved to Firestore');
                      }
                    }
                  } catch (firestoreError: any) {
                    console.error('❌ App: Error saving business to Firestore:', firestoreError);
                    console.warn('⚠️  Business saved locally but not in Firestore - will not be searchable across browsers');
                  }
                  
                  // Send welcome email right after registration (async, don't block UI)
                  welcomeEmailService.sendWelcomeEmail({
                    id: user.id,
                    email: user.email,
                    businessName: companyData.companyName,
                    name: companyData.companyName,
                  }).catch(error => {
                    console.error('Failed to send business owner welcome email:', error);
                    // Don't show error to user - email sending failure shouldn't block registration
                  });
                  
                  // Now proceed to login
                  handleLoginSuccess('business-owner');
                } catch (error: any) {
                  console.error('❌ App: Error registering business owner:', error);
                  const errorMessage = error.message || 'Failed to create business owner account. Please try again.';
                  
                  // Check if email is already registered (check both error code and message)
                  if (error.code === 'auth/email-already-in-use' ||
                      errorMessage.toLowerCase().includes('already registered') || 
                      errorMessage.toLowerCase().includes('email-already-in-use')) {
                    Alert.alert(
                      'Email Already Registered',
                      'This email exists in Firebase Auth (possibly from the old database).\n\n' +
                      'Options:\n' +
                      '1. Sign in with your password\n' +
                      '2. Use "Forgot Password" if you don\'t remember it\n' +
                      '3. Contact support to delete the account if you need to re-register\n\n' +
                      'Note: The account may exist in Auth but not in Firestore (new database).',
                      [
                        {
                          text: 'Cancel',
                          style: 'cancel',
                        },
                        {
                          text: 'Go to Login',
                          onPress: () => {
                            // Navigate back to login screen
                            setCurrentScreen('login');
                          },
                        },
                      ]
                    );
                  } else {
                    Alert.alert('Registration Error', errorMessage);
                  }
                }
              }}
            />
          );
        case 'care-invite-accept':
          return careInviteParams ? (
            <CareInviteAcceptScreen
              recipientId={careInviteParams.recipientId}
              memberId={careInviteParams.memberId}
              token={careInviteParams.token}
              onAcceptSuccess={() => {
                setCareInviteParams(null);
                setCurrentScreen('care-dashboard');
              }}
              onBack={() => {
                setCareInviteParams(null);
                setCurrentScreen('login');
              }}
              onNavigateToLogin={() => setCurrentScreen('login')}
            />
          ) : (
            <LoginScreen
              onLoginSuccess={handleLoginSuccess}
              onCreateAccount={handleNavigateToUserTypeSelection}
              onHelp={handleNavigateToHelp}
            />
          );
        case 'staff-invite-accept':
          return staffInviteParams ? (
            <StaffInviteAcceptScreen
              inviteId={staffInviteParams.inviteId}
              token={staffInviteParams.token}
              onAcceptSuccess={() => {
                setStaffInviteParams(null);
                setCurrentScreen('user-dashboard');
              }}
              onBack={() => {
                setStaffInviteParams(null);
                setCurrentScreen('login');
              }}
              onNavigateToLogin={() => setCurrentScreen('login')}
            />
          ) : (
            <LoginScreen
              onLoginSuccess={handleLoginSuccess}
              onCreateAccount={handleNavigateToUserTypeSelection}
              onHelp={handleNavigateToHelp}
            />
          );
        case 'login':
          return (
            <LoginScreen 
              onLoginSuccess={handleLoginSuccess}
              onCreateAccount={handleNavigateToUserTypeSelection}
              onHelp={handleNavigateToHelp}
            />
          );
      }
    }

    // Route based on user type
    if (userType === 'customer') {
      switch (currentScreen) {
        case 'staff-invite-accept':
          return staffInviteParams ? (
            <StaffInviteAcceptScreen
              inviteId={staffInviteParams.inviteId}
              token={staffInviteParams.token}
              onAcceptSuccess={() => {
                setStaffInviteParams(null);
                setCurrentScreen('user-dashboard');
              }}
              onBack={() => {
                setStaffInviteParams(null);
                setCurrentScreen(careRole ? 'care-dashboard' : 'user-dashboard');
              }}
            />
          ) : (
            <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
              <Text>No invite data</Text>
            </View>
          );
        case 'care-invite-accept':
          return careInviteParams ? (
            <CareInviteAcceptScreen
              recipientId={careInviteParams.recipientId}
              memberId={careInviteParams.memberId}
              token={careInviteParams.token}
              onAcceptSuccess={() => {
                setCareInviteParams(null);
                setCareRole('family');
                setCurrentScreen('care-dashboard');
              }}
              onBack={() => {
                setCareInviteParams(null);
                setCurrentScreen(careRole ? 'care-dashboard' : 'user-dashboard');
              }}
            />
          ) : (
            <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
              <Text>No invite data</Text>
            </View>
          );
        case 'care-notes':
          return (
            <CareNotesScreen
              recipientId={careNotesRecipientId}
              recipientName={careNotesRecipientName}
              currentUserName={username}
              onBack={() => setCurrentScreen('care-dashboard')}
              canEdit={careNotesCanEdit}
            />
          );
        case 'user-profile':
          return careRole ? (
            <UserProfileScreen
              onBack={() => setCurrentScreen('care-dashboard')}
              onLogout={handleLogout}
            />
          ) : null;
        case 'care-dashboard':
          return careRole ? (
            <CareDashboardScreen
              careRole={careRole}
              onNavigateToSettings={handleNavigateToSettings}
              onNavigateToAccount={() => setCurrentScreen('user-profile')}
              onLogout={handleLogout}
              onNavigateToBookAppointment={() => setCurrentScreen('business-exploration')}
              onNavigateToNotes={(recipientId, recipientName, canEdit) => {
                setCareNotesRecipientId(recipientId);
                setCareNotesRecipientName(recipientName);
                setCareNotesCanEdit(canEdit);
                setCurrentScreen('care-notes');
              }}
            />
          ) : (
            <UserDashboardScreen
              onLogout={handleLogout}
              onNavigateToChat={handleNavigateToChat}
              username={username}
              pendingBookFromFavorites={pendingBookFromFavorites}
              onPendingBookFromFavoritesConsumed={() => setPendingBookFromFavorites(null)}
              widgetBookingParams={widgetBookingParams}
              onWidgetBookingConsumed={() => setWidgetBookingParams(null)}
            />
          );
        case 'user-favorites':
          return (
            <UserFavoritesScreen
              onExploreBusinesses={() => setCurrentScreen('business-exploration')}
              onBookBusiness={(business) => {
                setPendingBookFromFavorites(business);
                setCurrentScreen('user-dashboard');
              }}
              onTabPress={(tab) => {
                if (tab === 'dashboard') setCurrentScreen(careRole ? 'care-dashboard' : 'chat');
                else if (tab === 'appointments') setCurrentScreen(careRole ? 'care-dashboard' : 'user-dashboard');
                else if (tab === 'favorites') setCurrentScreen('user-favorites');
                else if (tab === 'profile') setCurrentScreen(careRole ? 'care-dashboard' : 'user-dashboard');
              }}
              onLogout={handleLogout}
            />
          );
        case 'business-exploration':
          return (
            <BusinessExplorationScreen
              onBack={() => setCurrentScreen(careRole ? 'care-dashboard' : 'user-favorites')}
              onBusinessSelect={(business) => {
                setSelectedBusiness(business);
                setCurrentScreen('business-detail');
              }}
              onTabPress={(tab) => {
                if (tab === 'dashboard') setCurrentScreen(careRole ? 'care-dashboard' : 'chat');
                else if (tab === 'appointments') setCurrentScreen(careRole ? 'care-dashboard' : 'user-dashboard');
                else if (tab === 'favorites') setCurrentScreen('user-favorites');
                else if (tab === 'profile') setCurrentScreen(careRole ? 'care-dashboard' : 'user-dashboard');
              }}
              onLogout={handleLogout}
            />
          );
        case 'business-detail':
          return selectedBusiness ? (
            <BusinessDetailScreen
              business={selectedBusiness}
              onBack={() => setCurrentScreen('business-exploration')}
              onRequestSubmitted={() => {
                setCurrentScreen(careRole ? 'care-dashboard' : 'business-exploration');
              }}
            />
          ) : (
            <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
              <Text>No business selected</Text>
            </View>
          );
        case 'chat':
          return <BookAPTChatScreen onBack={() => setCurrentScreen('user-dashboard')} onTabPress={(tab) => {
            if (tab === 'dashboard') {
              // Home button should always go to user dashboard, not chat
              setCurrentScreen('user-dashboard');
            } else if (tab === 'appointments') setCurrentScreen('user-dashboard');
            else if (tab === 'favorites') setCurrentScreen('user-favorites');
            else if (tab === 'profile') setCurrentScreen('user-dashboard');
          }} onLogout={handleLogout} />;
        case 'customer-chat':
          return (
            <UserDashboardScreen
              onLogout={handleLogout}
              onNavigateToChat={handleNavigateToChat}
              username={username}
              pendingBookFromFavorites={pendingBookFromFavorites}
              onPendingBookFromFavoritesConsumed={() => setPendingBookFromFavorites(null)}
              widgetBookingParams={widgetBookingParams}
              onWidgetBookingConsumed={() => setWidgetBookingParams(null)}
            />
          );
        case 'user-dashboard':
        default:
          return (
            <UserDashboardScreen
              onLogout={handleLogout}
              onNavigateToChat={handleNavigateToChat}
              username={username}
              triggerOneClickBooking={triggerOneClickBooking}
              onOneClickBookingTriggered={() => setTriggerOneClickBooking(false)}
              pendingBookFromFavorites={pendingBookFromFavorites}
              onPendingBookFromFavoritesConsumed={() => setPendingBookFromFavorites(null)}
              widgetBookingParams={widgetBookingParams}
              onWidgetBookingConsumed={() => setWidgetBookingParams(null)}
              initialActiveTab={dashboardInitialTab}
              onInitialActiveTabConsumed={() => setDashboardInitialTab(undefined)}
            />
          );
      }
    }

    // Business owner and super admin screens
    switch (currentScreen) {
      case 'company-setup':
        return (
          <CompanySetupScreen
            onComplete={handleCompanySetupComplete}
            onBack={() => setCurrentScreen('login')}
          />
        );
      case 'package-selection':
        return (
          <PackageSelectionScreen
            onSelect={handlePackageSelectionComplete}
            onBack={() => setCurrentScreen('dashboard')}
          />
        );
      case 'super-admin':
        return (
          <SuperAdminScreen
            onLogout={handleLogout}
            onScanBusinessCard={() => setCurrentScreen('scan-business-card')}
          />
        );
      case 'scan-business-card':
        return <ScanBusinessCardScreen onBack={() => setCurrentScreen('super-admin')} />;
      case 'settings':
        return <SettingsScreen onNavigateToServiceManagement={handleNavigateToServiceManagement} onNavigateToProductManagement={handleNavigateToProductManagement} onNavigateToPaymentProviders={handleNavigateToPaymentProviders} onNavigateToSubscription={() => setCurrentScreen('package-selection')} onLogout={handleLogout} onBack={() => setCurrentScreen('dashboard')} />;
      case 'service-management':
        return <ServiceManagementScreen onBack={() => setCurrentScreen('dashboard')} />;
      case 'product-management':
        return (
          <ProductManagementScreen 
            onBack={() => setCurrentScreen('dashboard')} 
            onNavigateToSubscription={() => setCurrentScreen('package-selection')}
          />
        );
      case 'qr-code':
        return <QRCodePaymentScreen onBack={() => setCurrentScreen('dashboard')} businessId={username || 'test_business_1'} businessName="Elite Hair Studio" />;
      case 'customer-management':
        return <CustomerManagementScreen onBack={() => setCurrentScreen('dashboard')} />;
      case 'payment-providers':
        return (
          <PaymentProvidersWrapper
            onBack={() => setCurrentScreen('settings')}
          />
        );
      case 'bank-setup':
        return <BankAccountSetupScreen onBack={() => setCurrentScreen('settings')} onComplete={() => setCurrentScreen('payout-dashboard')} />;
      case 'payout-dashboard':
        return <PayoutDashboardScreen onBack={() => setCurrentScreen('settings')} />;
      case 'chat':
        return <BookAPTChatScreen onBack={() => setCurrentScreen('dashboard')} onTabPress={(tab) => {
          if (tab === 'dashboard') setCurrentScreen('dashboard');
          else if (tab === 'appointments') setCurrentScreen('dashboard');
          else if (tab === 'favorites') setCurrentScreen('dashboard');
          else if (tab === 'profile') setCurrentScreen('dashboard');
        }} onLogout={handleLogout} />;
      case 'business-chat':
        return (
          <BusinessChatScreen
            onBack={() => setCurrentScreen('dashboard')}
          />
        );
      case 'send-invoice':
        return (
          <SendInvoiceScreen
            onBack={() => setCurrentScreen('dashboard')}
            onNavigateToBusinessChat={handleNavigateToBusinessChat}
          />
        );
      case 'widget-download':
        return (
          <WidgetDownloadScreen
            onBack={() => setCurrentScreen('dashboard')}
            username={username}
          />
        );
      case 'quote-requests':
        return <QuoteRequestsScreen onBack={() => setCurrentScreen('dashboard')} />;
      case 'dashboard':
      default:
        return <DashboardScreen onLogout={handleLogout} onNavigateToSettings={handleNavigateToSettings} onNavigateToServiceManagement={handleNavigateToServiceManagement} onNavigateToProductManagement={handleNavigateToProductManagement} onNavigateToQRCode={handleNavigateToQRCode} onNavigateToCustomerManagement={handleNavigateToCustomerManagement} onNavigateToBankSetup={handleNavigateToBankSetup} onNavigateToPayoutDashboard={handleNavigateToPayoutDashboard} onNavigateToBusinessChat={handleNavigateToBusinessChat} onNavigateToSendInvoice={handleNavigateToSendInvoice} onNavigateToWidgetDownload={handleNavigateToWidgetDownload} onNavigateToQuoteRequests={handleNavigateToQuoteRequests} onNavigateToPackageSelection={() => setCurrentScreen('package-selection')} username={username} />;
    }
  };

  if (webE2ePrepay) {
    return (
      <View style={styles.container}>
        <PrepayBookingE2EHarness />
      </View>
    );
  }

  if (showSplash) {
    return <SplashScreen onFinish={handleSplashFinish} />;
  }

  return (
    StripeProvider ? (
      <StripeProvider
        publishableKey={APP_STRIPE_CONFIG.publishableKey}
        merchantIdentifier="merchant.com.bookapt"
      >
        <View style={styles.container}>
          <MobileAppBanner />
          {renderScreen()}
        </View>
      </StripeProvider>
    ) : (
      <View style={styles.container}>
        <MobileAppBanner />
        {renderScreen()}
      </View>
    )
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#1b2838',
  },
});

export default App;